(function () {
  'use strict';

  var store = window.WmpStore;
  var router = window.WmpRouter;
  var MOBILE_HELP_ID = 'wmp-mobile-help-rx-extra';
  // Whether the user has pressed Continue at least once (ran validation).
  // Until this is true, keep the Continue button enabled so users can
  // trigger the first validation run. After a failed validation attempt
  // we require user interaction before enabling Continue again.
  var continueAttempted = false;

  /* -----------------------------------------------------------------------
     Dropdown range generators
     ----------------------------------------------------------------------- */
  function rangeOptions(min, max, step, formatFn) {
    var opts = [];
    for (var v = min; v <= max + 0.001; v += step) {
      var rounded = Math.round(v * 100) / 100;
      opts.push({ value: rounded, label: formatFn(rounded) });
    }
    return opts;
  }

  function signedFormat(v) {
    var s = Math.abs(v).toFixed(2);
    if (v > 0) return '+' + s;
    if (v < 0) return '-' + s;
    return s;
  }

  function pdFormat(v) {
    return v.toFixed(1);
  }

  function halfPdFormat(v) {
    return v.toFixed(2);
  }

  function intFormat(v) {
    return String(Math.round(v));
  }

  function prismFormat(v) { return v.toFixed(2); }

  function addFormat(v) { return '+' + v.toFixed(2); }

  /* Prism is optional and only available when its section block resolves to a
     real product variant. Never invent a surcharge without a purchasable item. */
  function getPrismConfig() {
    var cfg = window.WmpProductsConfig;
    var prism = cfg && typeof cfg.getPrism === 'function' ? cfg.getPrism() : null;
    return prism && prism.variantId ? prism : null;
  }

  function getPrismPriceCents() {
    var prism = getPrismConfig();
    return prism ? Number(prism.priceCents || 0) : 0;
  }

  /* Ranges below mirror the old prescription flow exactly — see
     /snippets/{sph,cyl,axis,pd,prism,prism-direction,add}-elements.liquid.
     Clinicians built the workflow around these bounds; widening them masked
     misreadings of legitimate RX values, so keep the new flow in lock-step. */
  var PRISM_V_OPTIONS = rangeOptions(0, 8, 0.25, prismFormat);
  var PRISM_H_OPTIONS = rangeOptions(0, 8, 0.25, prismFormat);
  var PRISM_V_DIR_OPTIONS = [{ value: 'Up', label: 'Up' }, { value: 'Down', label: 'Down' }];
  var PRISM_H_DIR_OPTIONS = [{ value: 'In', label: 'In' }, { value: 'Out', label: 'Out' }];

  var SPH_OPTIONS = rangeOptions(-8, 8, 0.25, signedFormat);
  var CYL_OPTIONS = rangeOptions(-5, 5, 0.25, signedFormat);
  var AXIS_OPTIONS = rangeOptions(1, 180, 1, intFormat);
  var ADD_OPTIONS = rangeOptions(0.25, 4.0, 0.25, addFormat);
  // The lab can't make progressive lenses below +0.50 ADD; Reading keeps +0.25.
  var PROGRESSIVE_ADD_OPTIONS = rangeOptions(0.5, 4.0, 0.25, addFormat);
  var PD_OPTIONS = rangeOptions(50, 80, 1.0, pdFormat);
  var HALF_PD_OPTIONS = rangeOptions(25, 40, 0.5, halfPdFormat);

  /* Precompute <option> HTML strings once so each select is populated via a
     single innerHTML assignment instead of ~N appendChild calls into a live
     DOM. This is the hot path on first Step 2 render (~930 options total).

     Per-field builders mirror the old flow's snippet rendering exactly:
       - SPH/CYL/Prism V/H: no leading placeholder, zero preselected.
       - SPH duplicates the zero option as "Plano" (physician shorthand).
         Both rows carry value="0" so parseFloat returns 0 either way.
         CYL omits the Plano duplicate (clinicians don't use it there).
       - AXIS/ADD/PD/half-PD: leading dash/PD label as a visual
         placeholder. The placeholder's value stays "" so the
         `sel.value === ''` guards in collectValues()/hasFormValues()
         keep treating it as "not selected".
       - Prism direction: n/a serves as both the placeholder and the
         "no direction" semantic value; selecting it submits "". */

  function buildSignedZeroSelected(options, zeroLabel, includePlano) {
    if (includePlano === undefined) includePlano = true;
    var html = '';
    for (var i = 0; i < options.length; i++) {
      var v = options[i].value;
      if (v === 0) {
        html += '<option value="0" selected>' + zeroLabel + '</option>';
        if (includePlano) html += '<option value="0">Plano</option>';
      } else {
        html += '<option value="' + v + '">' + options[i].label + '</option>';
      }
    }
    return html;
  }

  function buildWithDashPlaceholder(options) {
    var html = '<option value="" selected>-</option>';
    for (var i = 0; i < options.length; i++) {
      html += '<option value="' + options[i].value + '">' + options[i].label + '</option>';
    }
    return html;
  }

  function buildPdPlaceholder(options) {
    var html = '';
    for (var i = 0; i < options.length; i++) {
      var v = options[i].value;
      var selected = v === 62 ? ' selected' : '';
      html += '<option value="' + v + '"' + selected + '>' + options[i].label + '</option>';
    }
    return html;
  }

  function buildPrismZeroSelected(options) {
    var html = '';
    for (var i = 0; i < options.length; i++) {
      var v = options[i].value;
      var sel = v === 0 ? ' selected' : '';
      html += '<option value="' + v + '"' + sel + '>' + options[i].label + '</option>';
    }
    return html;
  }

  function buildPrismDirOptions(options) {
    var html = '<option value="" selected>n/a</option>';
    for (var i = 0; i < options.length; i++) {
      html += '<option value="' + options[i].value + '">' + options[i].label + '</option>';
    }
    return html;
  }

  var OPTIONS_HTML = {
    sph: buildSignedZeroSelected(SPH_OPTIONS, '0 (PL)'),
    cyl: buildSignedZeroSelected(CYL_OPTIONS, '0 (DS)', false),
    axis: buildWithDashPlaceholder(AXIS_OPTIONS),
    add: buildWithDashPlaceholder(ADD_OPTIONS),
    addProgressive: buildWithDashPlaceholder(PROGRESSIVE_ADD_OPTIONS),
    pd: buildPdPlaceholder(PD_OPTIONS),
    halfPd: buildWithDashPlaceholder(HALF_PD_OPTIONS),
    prismV: buildPrismZeroSelected(PRISM_V_OPTIONS),
    prismH: buildPrismZeroSelected(PRISM_H_OPTIONS),
    prismVDir: buildPrismDirOptions(PRISM_V_DIR_OPTIONS),
    prismHDir: buildPrismDirOptions(PRISM_H_DIR_OPTIONS)
  };

  /* SVGs */
  function iconImgHtml(url) {
    if (!url) return '';
    return '<img src="' + url + '" alt="" width="20" height="20" loading="eager" aria-hidden="true">';
  }

  var containerRef = null;
  var continueBtnRef = null;
  var _savedPrescriptionApplied = false;
  var _savedPrescriptionData = null;
  var _savedPrescriptionCheckPromise = null;

  function buildOrderDetailsSection(rxValues, infoIconHtml) {
    var orderRx = (rxValues && rxValues.orderAttributes) || {};
    var saveDetailsSection = document.createElement('div');
    saveDetailsSection.className = 'wmp-rx-canada wmp-rx-order-attrs';

    var saveDetailsHeader = document.createElement('div');
    saveDetailsHeader.className = 'wmp-rx-order-attrs__header';

    var saveDetailsTitle = document.createElement('span');
    saveDetailsTitle.className = 'title';
    saveDetailsTitle.textContent = 'Let’s set up your prescription. We’ll need a few details first';
    saveDetailsHeader.appendChild(saveDetailsTitle);

    saveDetailsSection.appendChild(saveDetailsHeader);

    var saveDetailsSubtitle = document.createElement('div');
    saveDetailsSubtitle.className = 'wmp-rx-order-attrs__subtitle';

    var saveDetailsSubtitleText = document.createElement('span');
    saveDetailsSubtitleText.className = 'text';
    saveDetailsSubtitleText.textContent = 'Why we ask this information';
    saveDetailsSubtitle.appendChild(saveDetailsSubtitleText);

    var saveDetailsInfo = document.createElement('span');
    saveDetailsInfo.className = 'wmp-card__info';
    saveDetailsInfo.innerHTML = infoIconHtml;
    saveDetailsInfo.setAttribute('role', 'button');
    saveDetailsInfo.setAttribute('tabindex', '0');
    saveDetailsInfo.addEventListener('click', function (e) {
      e.stopPropagation();
      var content = 'Canadian tax regulations require prescription eyewear to be supplied based on a valid prescription in order to qualify as a zero-rated medical device (no GST/HST). We must collect prescription information issued by a licensed eye-care professional to process your order.';
      WmpInfoBubble.show(e.currentTarget, { content: content });
    });
    saveDetailsInfo.addEventListener('mouseenter', function (e) {
      var content = 'Canadian tax regulations require prescription eyewear to be supplied based on a valid prescription in order to qualify as a zero-rated medical device (no GST/HST). We must collect prescription information issued by a licensed eye-care professional to process your order.';
      WmpInfoBubble.show(e.currentTarget, { content: content });
    });
    saveDetailsInfo.addEventListener('mouseleave', function () {
      WmpInfoBubble.hide();
    });
    saveDetailsSubtitle.appendChild(saveDetailsInfo);
    saveDetailsSection.appendChild(saveDetailsSubtitle);

    var saveDetailsFieldsTitle = document.createElement('div');
    saveDetailsFieldsTitle.className = 'title';
    saveDetailsFieldsTitle.style = 'margin-bottom: 12px;';
    saveDetailsFieldsTitle.textContent = 'Issue date of prescription';
    saveDetailsSection.appendChild(saveDetailsFieldsTitle);

    var saveDetailsFields = document.createElement('div');
    saveDetailsFields.className = 'wmp-rx-canada__fields';

    function toMmYyyyDisplay(value) {
      if (!value) return '';
      var v = String(value);
      if (v.indexOf('-') !== -1) {
        var parts = v.split('-');
        if (parts.length === 2 && parts[0].length === 4) {
          return parts[1] + '/' + parts[0];
        }
      }
      return v;
    }

    var saveDateWrap = document.createElement('div');
    saveDateWrap.className = 'wmp-rx-canada__field';
    var saveDateInput = document.createElement('input');
    saveDateInput.type = 'text';
    saveDateInput.inputMode = 'numeric';
    saveDateInput.autocomplete = 'off';
    saveDateInput.maxLength = 7;
    saveDateInput.className = 'wmp-rx-input';
    saveDateInput.setAttribute('data-order-field', 'issueDate');
    saveDateInput.value = toMmYyyyDisplay(orderRx.issueDate || '');
    saveDateInput.placeholder = 'MM/YYYY';

    saveDateInput.addEventListener('input', function (e) {
      var digits = e.target.value.replace(/\D/g, '').slice(0, 6);
      e.target.value = digits.length > 2
        ? digits.slice(0, 2) + '/' + digits.slice(2)
        : digits;
    });

    saveDateWrap.appendChild(saveDateInput);
    saveDetailsFields.appendChild(saveDateWrap);

    var saveDoctorWrap = document.createElement('div');
    saveDoctorWrap.className = 'wmp-rx-canada__field';
    var saveDoctorInput = document.createElement('input');
    saveDoctorInput.type = 'text';
    saveDoctorInput.className = 'wmp-rx-input';
    saveDoctorInput.setAttribute('data-order-field', 'doctorName');
    saveDoctorInput.placeholder = "*Doctor's first and last name";
    saveDoctorInput.value = orderRx.doctorName || '';
    saveDoctorWrap.appendChild(saveDoctorInput);
    saveDetailsFields.appendChild(saveDoctorWrap);

    saveDetailsSection.appendChild(saveDetailsFields);

    var confirmWrap = document.createElement('label');
    confirmWrap.className = 'wmp-rx-order-attrs__confirm';
    var confirmInput = document.createElement('input');
    confirmInput.type = 'checkbox';
    confirmInput.className = 'wmp-checkbox';
    confirmInput.setAttribute('data-order-field', 'confirmed');
    confirmInput.checked = !!orderRx.confirmed;
    confirmWrap.appendChild(confirmInput);

    var textAndInfoContainer = document.createElement('span');
    textAndInfoContainer.className = 'wmp-rx-order-attrs__confirm-text';

    var confirmText = document.createElement('span');
    confirmText.className = 'text';
    confirmText.textContent = 'I confirm this prescription was issued to me and signed by a licensed eye-care professional.';
    textAndInfoContainer.appendChild(confirmText);

    var confirmInfo = document.createElement('span');
    confirmInfo.className = 'wmp-card__info';
    confirmInfo.innerHTML = infoIconHtml;
    confirmInfo.setAttribute('role', 'button');
    confirmInfo.setAttribute('tabindex', '0');
    confirmInfo.style.marginLeft = '4px';
    confirmInfo.addEventListener('click', function (e) {
      e.stopPropagation();
      var content = 'Canadian tax regulations require prescription eyewear to be supplied based on a valid prescription in order to qualify as a zero-rated medical device (no GST/HST). We must collect prescription information issued by a licensed eye-care professional to process your order.';
      WmpInfoBubble.show(e.currentTarget, { content: content });
    });
    confirmInfo.addEventListener('mouseenter', function (e) {
      var content = 'Canadian tax regulations require prescription eyewear to be supplied based on a valid prescription in order to qualify as a zero-rated medical device (no GST/HST). We must collect prescription information issued by a licensed eye-care professional to process your order.';
      WmpInfoBubble.show(e.currentTarget, { content: content });
    });
    confirmInfo.addEventListener('mouseleave', function () {
      WmpInfoBubble.hide();
    });
    textAndInfoContainer.appendChild(confirmInfo);
    confirmWrap.appendChild(textAndInfoContainer);
    saveDetailsSection.appendChild(confirmWrap);

    return saveDetailsSection;
  }

  function render(container) {
    containerRef = container;
    continueBtnRef = null;
    if (store && typeof store.hydrateRxFromSession === 'function') {
      store.hydrateRxFromSession();
    }
    var rxType = store.get('prescriptionType');
    var showAdd = rxType === 'progressive' || rxType === 'reading';
    var rxValues = store.get('rxValues');
    var prismConfig = getPrismConfig();

    // A saved/session prescription can outlive a removed Prism block. Clear
    // that stale purchase state so a hidden option never leaves a phantom fee.
    if (!prismConfig) {
      var currentPricing = store.get('pricing');
      if (rxValues.hasPrism || currentPricing.prism) {
        rxValues.hasPrism = false;
        currentPricing.prism = 0;
        store.set('rxValues', rxValues);
        store.set('pricing', currentPricing);
        store.recalcLenses();
      }
    }

    var root = document.getElementById('wmp-prescription-flow');
    var questionIconUrl = root ? root.getAttribute('data-question-icon') : '';
    var fileIconUrl = root ? root.getAttribute('data-file-icon') : '';
    var eyeIconUrl = root ? root.getAttribute('data-eye-icon') : '';
    var clipboardIconHtml = iconImgHtml(fileIconUrl);
    var clockIconHtml = iconImgHtml(eyeIconUrl);
    var infoIconHtml = questionIconUrl
      ? '<img src="' + questionIconUrl + '" alt="" aria-hidden="true" width="16" height="16" loading="eager">'
      : '?';

    // Heading row
    var header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:8px;margin-bottom:24px;';
    header.className = 'desktop-only';
    var heading = document.createElement('h2');
    heading.className = 'wmp-flow__step-heading';
    heading.textContent = store.getStepHeading('enter-prescription');
    heading.style.marginBottom = '0';
    header.appendChild(heading);

    var helpLink = document.createElement('button');
    helpLink.className = 'wmp-help-link';
    helpLink.innerHTML = 'How to read my prescription <span class="wmp-card__info" aria-hidden="true">' + infoIconHtml + '</span>';
    helpLink.type = 'button';
    helpLink.addEventListener('click', onRxHelpClick);
    header.appendChild(helpLink);

    container.appendChild(header);

    renderMobileStepHelp(infoIconHtml);

    // Action buttons (Saved RX / Expired RX)
    var actions = document.createElement('div');
    actions.className = 'wmp-rx-actions';

    var customer = store.get('customer') || {};
    var savedBtn;

    function openContactModal() {
      var i18n = window.WmpI18n || {};
      var strings = i18n.contact || {};

      var content = document.createElement('div');
      content.className = 'wmp-contact-form';

      var gorgiasContainer = document.createElement('div');
      gorgiasContainer.setAttribute('data-gorgias-contact-form', 'container');
      content.appendChild(gorgiasContainer);

      var gorgiasScript = document.createElement('script');
      gorgiasScript.defer = true;
      gorgiasScript.src = 'https://contact.gorgias.help/api/contact-forms/loader.js?uid=puujp111&locale=en-US';
      gorgiasScript.setAttribute('data-gorgias-contact-form', 'script');
      content.appendChild(gorgiasScript);

      WmpModal.open({
        title: strings.contact_title || 'CONTACT US',
        content: content,
        size: 'lg',
        dialogClass: 'wmp-modal-dialog--contact'
      });
    }

    if (customer.loggedIn) {
      // Start with informational state; switch to actionable once account data confirms a saved Rx.
      savedBtn = createActionBtn(clipboardIconHtml, '', "You're Logged In", function () {
        if (!savedBtn.disabled) applySavedPrescriptionFromAccount(savedBtn);
      });
      setSavedRxButtonState(savedBtn, false);

      checkSavedPrescriptionAvailability().then(function (pres) {
        if (!containerRef || !savedBtn || !savedBtn.parentNode) return;
        setSavedRxButtonState(savedBtn, !!pres);
      });
    } else {
      // Logged out — show sign-in button
      savedBtn = createActionBtn(clipboardIconHtml, 'Saved RX?', 'Sign In', function () {
        openAuthModal();
      });
    }
    actions.appendChild(savedBtn);

    var showExpiredRx = (root && root.getAttribute('data-show-expired-rx')) !== 'false';
    var expiredRxLabel = (root && root.getAttribute('data-expired-rx-label')) || 'Expired RX?';
    var expiredRxCta = (root && root.getAttribute('data-expired-rx-cta')) || 'Renew Online';

    if (showExpiredRx) {
      var expiredBtn = createActionBtn(clockIconHtml, expiredRxLabel, expiredRxCta, function () {
        var flowRoot = document.getElementById('wmp-prescription-flow');
        var renewUrl = (flowRoot && flowRoot.getAttribute('data-renew-rx-url')) || '/pages/renew-your-expired-prescription';
        if (renewUrl) {
          window.open(renewUrl, '_blank');
        }
      });
      actions.appendChild(expiredBtn);
    }

    container.appendChild(actions);

    // Error container (global)
    var errorBox = document.createElement('div');
    errorBox.className = 'wmp-rx-errors';
    errorBox.id = 'wmp-rx-errors';
    container.appendChild(errorBox);

    // Prescription grid (table-like structure matching Figma)
    var table = document.createElement('table');
    table.className = 'wmp-rx-table';
    if (!showAdd) table.classList.add('wmp-rx-table--4-cols');

    var colCount = showAdd ? 5 : 4; // label + SPH + CYL + AXIS [+ ADD]

    // Header row
    var thead = document.createElement('thead');
    var htr = document.createElement('tr');
    htr.innerHTML = '<th class="wmp-rx-table__corner"></th>' +
      '<th>SPH</th><th>CYL</th><th>AXIS</th>' +
      (showAdd ? '<th>ADD</th>' : '');
    thead.appendChild(htr);
    table.appendChild(thead);

    // Body rows
    var tbody = document.createElement('tbody');
    tbody.appendChild(buildEyeRow('od', 'OD', 'Right Eye', showAdd, rxValues.od));
    tbody.appendChild(buildEyeRow('os', 'OS', 'Left Eye', showAdd, rxValues.os));
    table.appendChild(tbody);

    container.appendChild(table);

    // PD section — horizontal pill layout matching Figma
    var pdSection = document.createElement('div');
    pdSection.className = 'wmp-rx-pd';

    var pdLabelCell = document.createElement('div');
    pdLabelCell.className = 'wmp-rx-pd__label-cell';
    pdLabelCell.innerHTML = '<strong>PD</strong><span>Pupillary Distance</span>';
    pdSection.appendChild(pdLabelCell);

    var pdLabelEmptyCell = document.createElement('div');
    pdLabelEmptyCell.className = 'wmp-rx-pd__label-empty';
    pdLabelEmptyCell.id = 'wmp-pd-label-empty';
    pdLabelEmptyCell.hidden = !rxValues.hasTwoPDs;
    pdSection.appendChild(pdLabelEmptyCell);

    var pdValueCell = document.createElement('div');
    pdValueCell.className = 'wmp-rx-pd__value-cell';
    pdValueCell.id = 'wmp-pd-row';

    var pdSelect = createSelect('pd', 'pd', rxValues.pd);
    pdValueCell.appendChild(pdSelect);

    pdSection.appendChild(pdValueCell);

    var lpdHeaderCell = document.createElement('div');
    lpdHeaderCell.className = 'wmp-rx-pd__header-cell';
    lpdHeaderCell.id = 'wmp-lpd-header';
    lpdHeaderCell.textContent = 'Left PD (OS)';
    lpdHeaderCell.hidden = !rxValues.hasTwoPDs;
    pdSection.appendChild(lpdHeaderCell);

    var rpdHeaderCell = document.createElement('div');
    rpdHeaderCell.className = 'wmp-rx-pd__header-cell';
    rpdHeaderCell.id = 'wmp-rpd-header';
    rpdHeaderCell.textContent = 'Right PD (OD)';
    rpdHeaderCell.hidden = !rxValues.hasTwoPDs;
    pdSection.appendChild(rpdHeaderCell);

    var lpdValueCell = document.createElement('div');
    lpdValueCell.className = 'wmp-rx-pd__value-cell wmp-rx-pd__value-cell--split';
    lpdValueCell.id = 'wmp-lpd-row';
    lpdValueCell.hidden = !rxValues.hasTwoPDs;
    lpdValueCell.appendChild(createSelect('lpd', 'halfPd', rxValues.lpd));
    pdSection.appendChild(lpdValueCell);

    var rpdValueCell = document.createElement('div');
    rpdValueCell.className = 'wmp-rx-pd__value-cell wmp-rx-pd__value-cell--split';
    rpdValueCell.id = 'wmp-rpd-row';
    rpdValueCell.hidden = !rxValues.hasTwoPDs;
    rpdValueCell.appendChild(createSelect('rpd', 'halfPd', rxValues.rpd));
    pdSection.appendChild(rpdValueCell);

    if (rxValues.hasTwoPDs) {
      pdValueCell.hidden = true;
      pdSection.classList.add('wmp-rx-pd--two-pds');
    }

    // 2-PD toggle
    var twoPdLabel = document.createElement('label');
    twoPdLabel.className = 'wmp-rx-pd__toggle';

    var twoPdCheck = document.createElement('input');
    twoPdCheck.type = 'checkbox';
    twoPdCheck.className = 'wmp-checkbox';
    twoPdCheck.id = 'wmp-two-pd-check';
    twoPdCheck.checked = rxValues.hasTwoPDs || false;
    twoPdCheck.addEventListener('change', onTwoPdToggle);
    twoPdLabel.appendChild(twoPdCheck);

    var twoPdText = document.createElement('span');
    twoPdText.textContent = 'My prescription contains 2 PDs';
    twoPdLabel.appendChild(twoPdText);

    var twoPdInfo = document.createElement('span');
    twoPdInfo.className = 'wmp-card__info';
    twoPdInfo.innerHTML = infoIconHtml;
    twoPdInfo.setAttribute('role', 'button');
    twoPdInfo.setAttribute('tabindex', '0');
    twoPdInfo.style.marginLeft = '4px';
    twoPdInfo.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (window.WmpApp && typeof window.WmpApp.openTwoPdsModal === 'function') {
        window.WmpApp.openTwoPdsModal();
      }
    });
    twoPdInfo.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      if (window.WmpApp && typeof window.WmpApp.openTwoPdsModal === 'function') {
        window.WmpApp.openTwoPdsModal();
      }
    });
    twoPdLabel.appendChild(twoPdInfo);

    // Wrap PD pill + toggle in a flex row (side-by-side desktop, stacked mobile)
    var pdWrapper = document.createElement('div');
    pdWrapper.className = 'wmp-rx-pd-wrapper';
    pdWrapper.appendChild(pdSection);
    pdWrapper.appendChild(twoPdLabel);
    container.appendChild(pdWrapper);

    // Hide the entire prism affordance unless checkout can add its variant.
    var prismCheck = null;
    if (prismConfig) {
      var prismLabel = document.createElement('label');
      prismLabel.className = 'wmp-rx-prism';

      prismCheck = document.createElement('input');
      prismCheck.type = 'checkbox';
      prismCheck.className = 'wmp-checkbox';
      prismCheck.id = 'wmp-prism-check';
      prismCheck.checked = rxValues.hasPrism || false;
      prismLabel.appendChild(prismCheck);

      var prismText = document.createElement('span');
      prismText.textContent = 'Prescription includes prism +' + store.formatPrice(getPrismPriceCents());
      prismLabel.appendChild(prismText);

      // (save checkbox will be appended after the prism label so it appears below)

      var prismInfo = document.createElement('span');
      prismInfo.className = 'wmp-card__info';
      prismInfo.innerHTML = infoIconHtml;
      prismInfo.setAttribute('role', 'button');
      prismInfo.setAttribute('tabindex', '0');
      prismInfo.setAttribute('aria-label', 'More information about prism prescription');
      prismInfo.setAttribute('aria-expanded', 'false');
      prismInfo.style.marginLeft = '4px';
      var showPrismInfo = function (trigger, toggle) {
        if (!window.WmpInfoBubble || !trigger) return;

        var i18n = window.WmpI18n || {};
        var info = i18n.info || {};
        WmpInfoBubble.show(trigger, {
          content: info.prism || '',
          toggle: toggle
        });
      };
      prismInfo.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        showPrismInfo(e.currentTarget);
      });
      prismInfo.addEventListener('mouseenter', function (e) {
        showPrismInfo(e.currentTarget, false);
      });
      prismInfo.addEventListener('focus', function (e) {
        showPrismInfo(e.currentTarget, false);
      });
      prismInfo.addEventListener('mouseleave', function (e) {
        if (document.activeElement !== e.currentTarget && window.WmpInfoBubble) WmpInfoBubble.hide();
      });
      prismInfo.addEventListener('blur', function () {
        if (window.WmpInfoBubble) WmpInfoBubble.hide();
      });
      prismLabel.appendChild(prismInfo);

      container.appendChild(prismLabel);
    }

    // Save Prescription checkbox (shown when logged in) follows the optional
    // prism control when that product is configured.
    if (customer.loggedIn) {
      var saveLabel = document.createElement('label');
      saveLabel.className = 'wmp-rx-prism'; // reuse same checkbox styling
      saveLabel.style.marginTop = '8px';

      var saveCheck = document.createElement('input');
      saveCheck.type = 'checkbox';
      saveCheck.className = 'wmp-checkbox';
      saveCheck.id = 'wmp-save-rx-check';
      saveLabel.appendChild(saveCheck);

      var saveText = document.createElement('span');
      var i18nAuth = (window.WmpI18n || {}).auth || {};
      saveText.textContent = i18nAuth.save_checkbox || 'Save prescription in account';
      saveLabel.appendChild(saveText);

      container.appendChild(saveLabel);
    }

    // Unified order details block: show only for Canadian region/host.
    (function () {
      var rootRegion = '';
      try {
        if (store && typeof store.get === 'function') rootRegion = store.get('region') || '';
      } catch (e) {
        rootRegion = '';
      }

      rootRegion = String(rootRegion).toLowerCase();

      var hostIsCa = false;
      try { hostIsCa = window.location.hostname.toLowerCase().endsWith('.ca'); } catch (e) { hostIsCa = false; }

      var navIsCa = false;
      try { navIsCa = !!(navigator && navigator.language && navigator.language.toLowerCase().indexOf('ca') !== -1); } catch (e) { navIsCa = false; }

      var isCanada = rootRegion === 'ca' || rootRegion === 'canada' || hostIsCa || navIsCa;

      if (isCanada) {
        container.appendChild(buildOrderDetailsSection(rxValues, infoIconHtml));
      }
    })();

    // Prism table (shown when the configured prism option is checked)
    if (prismConfig && prismCheck) {
      var prismTable = buildPrismTable(rxValues.prism || {});
      prismTable.id = 'wmp-prism-table';
      prismTable.hidden = !rxValues.hasPrism;
      container.appendChild(prismTable);

      prismCheck.addEventListener('change', function (e) {
        prismTable.hidden = !e.target.checked;
        if (!e.target.checked) clearPrismHighlights(container);
      });
    }

    // Help text
    var help = document.createElement('p');
    help.className = 'wmp-rx-help';
    help.innerHTML = 'Not sure about your prescription?<br><a href="#">Leave us a message</a>';
    // Attach handler to open contact modal when user clicks the link
    (function () {
      try {
        var a = help.querySelector('a');
        if (a) {
          a.addEventListener('click', function (e) {
            e.preventDefault();
            openContactModal();
          });
        }
      } catch (err) {
        // noop
      }
    })();
    container.appendChild(help);

    // Continue button
    var continueBtn = document.createElement('button');
    continueBtn.className = 'wmp-btn wmp-btn--primary wmp-btn--xl wmp-btn--full wmp-rx-continue-inline';
    continueBtn.setAttribute('type', 'button');
    continueBtn.textContent = 'CONTINUE';
    continueBtn.style.marginTop = '24px';
    continueBtn.addEventListener('click', onContinue);
    // Reflect any values restored from the store (e.g. when the user navigates
    // Back into this step) — no change event fires on re-render, so compute the
    // enabled state directly.
    syncContinueButtonState(container, continueBtn);
    continueBtnRef = continueBtn;
    container.appendChild(continueBtn);

    // Attach form change listener to enable button when values are filled
    attachFormChangeListener(container, continueBtn);

  }

  /* -----------------------------------------------------------------------
     Enable/disable button based on form input
     ----------------------------------------------------------------------- */
  // The Continue button gates on a real prescription value (at least one SPH or
  // CYL), mirroring validation rule #1. A preselected PD=62 must NOT enable it.
  function isSphOrCylField(fieldName) {
    return fieldName === 'od-sph' || fieldName === 'od-cyl' || fieldName === 'os-sph' || fieldName === 'os-cyl';
  }

  function isAddField(fieldName) {
    return fieldName === 'od-add' || fieldName === 'os-add';
  }

  function hasExplicitSphOrCylSelection(sel) {
    if (!sel) return false;
    var field = sel.getAttribute('data-field');
    if (!isSphOrCylField(field)) return false;

    if (sel.value === '') return false;

    // SPH/CYL are rendered with default zero selected. Treat this as "not
    // entered" until user explicitly changes the field or values are prefilled.
    var isDefaultZero = parseFloat(sel.value) === 0;
    if (!isDefaultZero) return true;

    return sel.getAttribute('data-user-set') === '1' || sel.getAttribute('data-prefilled') === '1';
  }

  function hasSphOrCylValues(container) {
    var names = ['od-sph', 'od-cyl', 'os-sph', 'os-cyl'];
    for (var i = 0; i < names.length; i++) {
      var sel = container.querySelector('[data-field="' + names[i] + '"]');
      if (hasExplicitSphOrCylSelection(sel)) return true;
    }
    return false;
  }

  function isCylEnteredForEye(container, eye) {
    var cylSel = container.querySelector('[data-field="' + eye + '-cyl"]');
    if (!hasExplicitSphOrCylSelection(cylSel)) return false;
    return parseFloat(cylSel.value) !== 0;
  }

  function isAxisFilledForEye(container, eye) {
    var axisSel = container.querySelector('[data-field="' + eye + '-axis"]');
    return !!(axisSel && axisSel.value !== '');
  }

  function eyeRequiresAxis(container, eye) {
    return isCylEnteredForEye(container, eye);
  }

  // Multifocal prescriptions always require ADD for both eyes.
  function isAddRequiredForEye(container, eye) {
    var rxType = store.get('prescriptionType');
    if (rxType !== 'progressive' && rxType !== 'reading') return false;
    return true;
  }

  function isAddFilledForEye(container, eye) {
    var addSel = container.querySelector('[data-field="' + eye + '-add"]');
    return !!(addSel && addSel.value !== '');
  }

  // Prism, when enabled, needs at least one non-zero Vertical/Horizontal value
  // before Continue unlocks (spec PRISM ERROR STATE).
  function prismSatisfied(container) {
    var prismCheck = document.getElementById('wmp-prism-check');
    if (!prismCheck || !prismCheck.checked) return true;
    var names = ['od-prism-v', 'od-prism-h', 'os-prism-v', 'os-prism-h'];
    for (var i = 0; i < names.length; i++) {
      var sel = container.querySelector('[data-field="' + names[i] + '"]');
      if (sel && sel.value !== '' && parseFloat(sel.value) !== 0) return true;
    }
    return false;
  }

  function isPrismMagnitudeFilled(container, fieldName) {
    if (!container || !fieldName) return false;
    var sel = container.querySelector('[data-field="' + fieldName + '"]');
    return !!(sel && sel.value !== '' && parseFloat(sel.value) !== 0);
  }

  function getPrismDirectionPairs() {
    return [
      { magnitude: 'od-prism-v', direction: 'od-prism-vdir', label: 'OD Vertical' },
      { magnitude: 'od-prism-h', direction: 'od-prism-hdir', label: 'OD Horizontal' },
      { magnitude: 'os-prism-v', direction: 'os-prism-vdir', label: 'OS Vertical' },
      { magnitude: 'os-prism-h', direction: 'os-prism-hdir', label: 'OS Horizontal' }
    ];
  }

  function syncPrismDirectionHighlights(container) {
    var prismCheck = document.getElementById('wmp-prism-check');
    if (!prismCheck || !prismCheck.checked) return;

    var pairs = getPrismDirectionPairs();
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i];
      var magFilled = isPrismMagnitudeFilled(container, pair.magnitude);
      var dirSel = container.querySelector('[data-field="' + pair.direction + '"]');
      if (!dirSel) continue;

      if (magFilled && dirSel.value === '') {
        setFieldErrorState(dirSel, true);
      } else {
        setFieldErrorState(dirSel, false);
      }
    }
  }

  function clearPrismMagnitudeHighlights(container) {
    var prismNames = ['od-prism-v', 'od-prism-h', 'os-prism-v', 'os-prism-h'];
    for (var i = 0; i < prismNames.length; i++) {
      var el = container.querySelector('[data-field="' + prismNames[i] + '"]');
      if (!el) continue;
      el.classList.remove('wmp-rx-field-error');
      var cell = el.closest && el.closest('.wmp-rx-table__cell');
      if (cell) cell.classList.remove('wmp-rx-field-error-cell');
    }
  }

  function highlightRequiredEmptyFields(container) {
    var rxType = store.get('prescriptionType');

    function markEmpty(fieldName, forceHighlight) {
      var el = container.querySelector('[data-field="' + fieldName + '"]') ||
               container.querySelector('[data-order-field="' + fieldName + '"]');
      if (!el) return;
      if (el.type === 'checkbox') {
        if (!forceHighlight && el.checked) return;
      } else if (!forceHighlight && el.value !== '') {
        return;
      }

      el.classList.add('wmp-rx-field-error');
      var cell = (el.closest && (
        el.closest('.wmp-rx-table__cell') ||
        el.closest('.wmp-rx-pd__value-cell') ||
        el.closest('.wmp-rx-canada__field') ||
        el.closest('.wmp-rx-order-attrs__confirm')
      )) || null;
      if (cell) cell.classList.add('wmp-rx-field-error-cell');
    }

    // SPH fields (at least one required — highlight all 4 if all empty)
    var sphOrCylFilled = hasSphOrCylValues(container);
    if (!sphOrCylFilled) {
      markEmpty('od-sph', true);
      markEmpty('od-cyl', true);
      markEmpty('os-sph', true);
      markEmpty('os-cyl', true);
    }

    // Progressive/Reading: ADD is required per-eye, whenever that eye's SPH
    // has an explicit value (see isAddRequiredForEye()).
    if (isAddRequiredForEye(container, 'od') && !isAddFilledForEye(container, 'od')) {
      markEmpty('od-add', true);
    }
    if (isAddRequiredForEye(container, 'os') && !isAddFilledForEye(container, 'os')) {
      markEmpty('os-add', true);
    }

    // CYL -> AXIS dependency (per-eye)
    if (eyeRequiresAxis(container, 'od') && !isAxisFilledForEye(container, 'od')) {
      markEmpty('od-axis', true);
    }
    if (eyeRequiresAxis(container, 'os') && !isAxisFilledForEye(container, 'os')) {
      markEmpty('os-axis', true);
    }

    // Single-PD mode: an actual binocular PD is required.
    var twoPdCheck = document.getElementById('wmp-two-pd-check');
    if (!twoPdCheck || !twoPdCheck.checked) {
      markEmpty('pd');
    }

    // Two-PD mode: both halves required
    if (twoPdCheck && twoPdCheck.checked) {
      markEmpty('rpd');
      markEmpty('lpd');
    }

    // Prism mode: require at least one non-zero vertical/horizontal value.
    // Red highlight appears only after user attempts to continue.
    var prismCheck = document.getElementById('wmp-prism-check');
    if (prismCheck && prismCheck.checked && !prismSatisfied(container)) {
      markEmpty('od-prism-v', true);
      markEmpty('od-prism-h', true);
      markEmpty('os-prism-v', true);
      markEmpty('os-prism-h', true);
    }

    // Unified order-details fields
    markEmpty('issueDate');
    markEmpty('doctorName');
    markEmpty('confirmed');
  }

  function setFieldErrorState(el, isError) {
    if (!el) return;

    if (isError) el.classList.add('wmp-rx-field-error');
    else el.classList.remove('wmp-rx-field-error');

    var cell = (el.closest && (
      el.closest('.wmp-rx-table__cell') ||
      el.closest('.wmp-rx-pd__value-cell') ||
      el.closest('.wmp-rx-canada__field') ||
      el.closest('.wmp-rx-order-attrs__confirm')
    )) || null;

    if (cell) {
      if (isError) cell.classList.add('wmp-rx-field-error-cell');
      else cell.classList.remove('wmp-rx-field-error-cell');
    }
  }

  function isFieldRequiredNow(container, fieldName, target) {
    var rxType = store.get('prescriptionType');

    if (isSphOrCylField(fieldName)) {
      return !hasSphOrCylValues(container);
    }

    if (fieldName === 'od-add') return isAddRequiredForEye(container, 'od');
    if (fieldName === 'os-add') return isAddRequiredForEye(container, 'os');

    if (fieldName === 'od-axis') return eyeRequiresAxis(container, 'od');
    if (fieldName === 'os-axis') return eyeRequiresAxis(container, 'os');

    if (fieldName === 'od-prism-vdir') return isPrismMagnitudeFilled(container, 'od-prism-v');
    if (fieldName === 'od-prism-hdir') return isPrismMagnitudeFilled(container, 'od-prism-h');
    if (fieldName === 'os-prism-vdir') return isPrismMagnitudeFilled(container, 'os-prism-v');
    if (fieldName === 'os-prism-hdir') return isPrismMagnitudeFilled(container, 'os-prism-h');

    if (fieldName === 'rpd' || fieldName === 'lpd') {
      var twoPdCheck = document.getElementById('wmp-two-pd-check');
      return !!(twoPdCheck && twoPdCheck.checked);
    }

    if (fieldName === 'issueDate') {
      return !!(container.querySelector('[data-order-field="issueDate"]') || container.querySelector('[data-order-field="doctorName"]'));
    }

    if (fieldName === 'doctorName') {
      return !!(container.querySelector('[data-order-field="issueDate"]') || container.querySelector('[data-order-field="doctorName"]'));
    }

    return false;
  }

  function isFieldEmptyForValidation(target) {
    if (!target) return true;

    var fieldName = target.getAttribute('data-field');
    if (fieldName && isSphOrCylField(fieldName)) {
      return !hasExplicitSphOrCylSelection(target);
    }

    if (target.tagName === 'SELECT') {
      return target.value === '';
    }

    if (target.type === 'checkbox') {
      return !target.checked;
    }

    return (target.value || '').trim() === '';
  }

  function clearPrismHighlights(container) {
    var prismNames = ['od-prism-v', 'od-prism-vdir', 'od-prism-h', 'od-prism-hdir', 'os-prism-v', 'os-prism-vdir', 'os-prism-h', 'os-prism-hdir'];
    for (var i = 0; i < prismNames.length; i++) {
      var el = container.querySelector('[data-field="' + prismNames[i] + '"]');
      if (!el) continue;
      el.classList.remove('wmp-rx-field-error');
      var cell = el.closest && el.closest('.wmp-rx-table__cell');
      if (cell) cell.classList.remove('wmp-rx-field-error-cell');
    }
  }

  function syncContinueButtonState(container, continueBtn) {
    continueBtn = continueBtn || continueBtnRef || null;
    var disabled = !canAttemptContinue(container);

    if (continueBtn) {
      continueBtn.disabled = disabled;
      if (disabled) continueBtn.setAttribute('disabled', 'disabled');
      else continueBtn.removeAttribute('disabled');
    }

    var mobileContinueBtn = document.getElementById('wmp-mobile-action');
    if (mobileContinueBtn) {
      mobileContinueBtn.disabled = disabled;
      if (disabled) mobileContinueBtn.setAttribute('disabled', 'disabled');
      else mobileContinueBtn.removeAttribute('disabled');
    }
  }

  // Continue acts as a validation trigger: enable it once the customer has
  // provided any meaningful input, then show missing required fields on click.
  function canAttemptContinue(container) {
    if (!container) return false;

    // If the user hasn't attempted Continue yet, allow the button to be
    // clickable so they can run validation. Once they've attempted and
    // validation failed, we require meaningful input to re-enable it.
    if (!continueAttempted) return true;

    // After a failed Continue, keep the button disabled while any required
    // field is still visually marked as invalid. This matches the flow where
    // Continue re-runs validation, instead of validating on each field edit.
    if (container.querySelector('.wmp-rx-field-error') || container.querySelector('.wmp-rx-field-error-cell')) {
      return false;
    }

    var fieldEls = container.querySelectorAll('select[data-field], [data-order-field]');
    for (var i = 0; i < fieldEls.length; i++) {
      var el = fieldEls[i];

      // Count explicit select interaction (or prefilled values restored from store)
      // as meaningful input. This avoids false positives from default options
      // like PD=62 and zeroed prism/SPH/CYL placeholders.
      if (el.tagName === 'SELECT') {
        if (el.getAttribute('data-user-set') === '1' || el.getAttribute('data-prefilled') === '1') {
          return true;
        }
        continue;
      }

      if (el.type === 'checkbox') {
        if (el.checked) return true;
        continue;
      }

      if ((el.value || '').trim() !== '') return true;
    }

    return false;
  }

  function isFormValid(container) {
    var rxType = store.get('prescriptionType');

    // Rule 1: At least one SPH or CYL value entered
    if (!hasSphOrCylValues(container)) return false;

    // Rule 2: Progressive/Reading requires ADD per eye whenever that eye's SPH
    // has an explicit value.
    if (isAddRequiredForEye(container, 'od') && !isAddFilledForEye(container, 'od')) return false;
    if (isAddRequiredForEye(container, 'os') && !isAddFilledForEye(container, 'os')) return false;

    // Rule 2a: CYL entered for an eye requires AXIS for that same eye.
    if (eyeRequiresAxis(container, 'od') && !isAxisFilledForEye(container, 'od')) return false;
    if (eyeRequiresAxis(container, 'os') && !isAxisFilledForEye(container, 'os')) return false;

    // Rule 3: PD is required in either single- or two-PD mode.
    var twoPdCheck = document.getElementById('wmp-two-pd-check');
    var hasTwoPd = !!(twoPdCheck && twoPdCheck.checked);
    if (!hasTwoPd) {
      var pdSel = container.querySelector('[data-field="pd"]');
      if (!pdSel || pdSel.value === '') return false;
    }

    if (hasTwoPd) {
      var rpdSel = container.querySelector('[data-field="rpd"]');
      var lpdSel = container.querySelector('[data-field="lpd"]');
      if (!rpdSel || rpdSel.value === '' || !lpdSel || lpdSel.value === '') return false;
    }

      // Rule 4: Order-details block requires issue date + doctor name.
      var orderIssueDate = container.querySelector('[data-order-field="issueDate"]');
      var orderDoctorName = container.querySelector('[data-order-field="doctorName"]');
      if ((orderIssueDate || orderDoctorName) &&
        (!orderIssueDate || orderIssueDate.value === '' ||
         !orderDoctorName || (orderDoctorName.value || '').trim() === '')) return false;

      // Rule 5: Order-details confirmation is required.
    var orderConfirmed = container.querySelector('[data-order-field="confirmed"]');
    if (orderConfirmed && !orderConfirmed.checked) return false;

    // Prism is validated on Continue (soft gate for button state): user must be
    // able to click Continue to trigger the red prism error state.

    return true;
  }

  function attachFormChangeListener(container, continueBtn) {
    container.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || !target.matches || !target.matches('select[data-field], [data-order-field]')) return;

      // if (target.matches('select[data-field]')) {
      //   target.setAttribute('data-user-set', '1');
      // }

      if (target.matches('select[data-field]')) {
        var f = target.getAttribute('data-field');
        if (isSphOrCylField(f) && parseFloat(target.value) === 0) {
          target.removeAttribute('data-user-set');
          target.removeAttribute('data-prefilled');
        } else {
          target.setAttribute('data-user-set', '1');
        }
      }

      target.classList.remove('wmp-rx-field-error');

      var cell = (target.closest && (
        target.closest('.wmp-rx-table__cell') ||
        target.closest('.wmp-rx-pd__value-cell') ||
        target.closest('.wmp-rx-canada__field') ||
        target.closest('.wmp-rx-order-attrs__confirm')
      )) || null;
      if (cell) cell.classList.remove('wmp-rx-field-error-cell');

      // Do not auto-focus AXIS after CYL changes. Users should explicitly tap
      // the AXIS field (especially on mobile where focus can open the picker).
      var field = target.getAttribute('data-field');

      // If CYL for an eye is cleared or set to zero, restore that eye's
      // AXIS to the original value saved at render time (per requirement).
      if (field === 'od-cyl' || field === 'os-cyl') {
        try {
          var eye = field.split('-')[0];
          var cylVal = target.value;
          if (cylVal === '' || parseFloat(cylVal) === 0) {
            var axisSel = container.querySelector('[data-field="' + eye + '-axis"]');
            if (axisSel) {
              var orig = axisSel.getAttribute('data-original') || '';
              axisSel.value = orig;
              axisSel.classList.remove('wmp-rx-field-error');
              var axisCell = axisSel.closest && axisSel.closest('.wmp-rx-table__cell');
              if (axisCell) axisCell.classList.remove('wmp-rx-field-error-cell');
            }
          }
        } catch (err) {
          /* noop */
        }
      }
      // If any SPH/CYL is now filled, clear the error highlight from all 4
      if (hasSphOrCylValues(container)) {
        var sphCylNames = ['od-sph', 'od-cyl', 'os-sph', 'os-cyl'];
        for (var j = 0; j < sphCylNames.length; j++) {
          var sc = container.querySelector('[data-field="' + sphCylNames[j] + '"]');
          if (sc) {
            sc.classList.remove('wmp-rx-field-error');
            var scCell = sc.closest && sc.closest('.wmp-rx-table__cell');
            if (scCell) scCell.classList.remove('wmp-rx-field-error-cell');
          }
        }
      }

      // If an eye's SPH just changed (e.g. reset back to default 0/Plano),
      // re-evaluate whether that eye's ADD is still required and clear its
      // highlight if it's no longer needed.
      if (field === 'od-sph' || field === 'os-sph') {
        var sphEye = field.split('-')[0];
        var sphAddStillRequired = isAddRequiredForEye(container, sphEye) && !isAddFilledForEye(container, sphEye);
        var sphAddEl = container.querySelector('[data-field="' + sphEye + '-add"]');
        if (sphAddEl && !sphAddStillRequired) {
          sphAddEl.classList.remove('wmp-rx-field-error');
          var sphAddCell = sphAddEl.closest && sphAddEl.closest('.wmp-rx-table__cell');
          if (sphAddCell) sphAddCell.classList.remove('wmp-rx-field-error-cell');
        }
      }

      // Progressive/Reading: clear an ADD field's required-highlight once it
      // is filled, or once it's no longer required for its eye.
      if (field === 'od-add' || field === 'os-add') {
        var addEye = field.split('-')[0];
        var addStillRequired = isAddRequiredForEye(container, addEye) && !isAddFilledForEye(container, addEye);
        if (!addStillRequired) {
          var addEl = container.querySelector('[data-field="' + field + '"]');
          if (addEl) {
            addEl.classList.remove('wmp-rx-field-error');
            var addCell = addEl.closest && addEl.closest('.wmp-rx-table__cell');
            if (addCell) addCell.classList.remove('wmp-rx-field-error-cell');
          }
        }
      }

      // Prism group rule: a filled magnitude makes its paired base direction
      // required, but only for the same eye + axis pair.
      if (field === 'od-prism-v' || field === 'od-prism-vdir' || field === 'od-prism-h' || field === 'od-prism-hdir' || field === 'os-prism-v' || field === 'os-prism-vdir' || field === 'os-prism-h' || field === 'os-prism-hdir') {
        if (prismSatisfied(container)) clearPrismMagnitudeHighlights(container);
        // Always update prism direction highlights immediately on change.
        syncPrismDirectionHighlights(container);
      }

      syncContinueButtonState(container, continueBtn);
    });

    // Show field-level error only after the customer leaves a required field empty.
    container.addEventListener('focusout', function (e) {
      var target = e.target;
      if (!target || !target.matches || !target.matches('select[data-field], [data-order-field]')) return;
      if (target.type === 'checkbox') return;

      var fieldName = target.getAttribute('data-field') ||
        target.getAttribute('data-order-field');
      if (!fieldName) return;

      var isPrismField = fieldName === 'od-prism-v' || fieldName === 'od-prism-vdir' || fieldName === 'od-prism-h' || fieldName === 'od-prism-hdir' || fieldName === 'os-prism-v' || fieldName === 'os-prism-vdir' || fieldName === 'os-prism-h' || fieldName === 'os-prism-hdir';

      if (!isFieldRequiredNow(container, fieldName, target)) {
        setFieldErrorState(target, false);
        if (isPrismField) syncPrismDirectionHighlights(container);
        return;
      }

      setFieldErrorState(target, isFieldEmptyForValidation(target));

      if (isPrismField) {
        syncPrismDirectionHighlights(container);
      }
    }, true);

    // Run the same validation as focusout, but on every keystroke too —
    // so error state updates live while typing, not just on blur.
    container.addEventListener('input', function (e) {
      var target = e.target;
      if (!target || !target.matches || !target.matches('[data-order-field]')) return;
      if (target.type === 'checkbox') return; // checkboxes use 'change', not 'input'

      var fieldName = target.getAttribute('data-order-field');
      if (!fieldName) return;

      if (!isFieldRequiredNow(container, fieldName, target)) {
        setFieldErrorState(target, false);
      } else {
        setFieldErrorState(target, isFieldEmptyForValidation(target));
      }

      syncContinueButtonState(container, continueBtn);
    });

    // Also attach listener to two-PD checkbox
    var twoPdCheck = document.getElementById('wmp-two-pd-check');
    if (twoPdCheck) {
      twoPdCheck.addEventListener('change', function () {
        syncContinueButtonState(container, continueBtn);
      });
    }

    // The prism checkbox isn't a [data-field] select, but toggling it changes
    // whether a prism value is required — re-evaluate the Continue gate.
    var prismCheck = document.getElementById('wmp-prism-check');
    if (prismCheck) {
      prismCheck.addEventListener('change', function (e) {
        syncContinueButtonState(container, continueBtn);

        // Reset prism-only error styles when prism is toggled off.
        if (!e.target.checked) {
          clearPrismHighlights(container);
        }
      });
    }
  }

  function renderMobileStepHelp(infoIconHtml) {
    removeMobileStepHelp();
    var mobileStepInfo = document.getElementById('wmp-mobile-step-info');
    if (!mobileStepInfo) return;

    var mobileHelp = document.createElement('button');
    mobileHelp.className = 'wmp-help-link-wrapper';
    mobileHelp.id = MOBILE_HELP_ID;
    mobileHelp.type = 'button';
    mobileHelp.innerHTML =
      '<span class="wmp-help-link">How to read my prescription</span>' +
      '<span class="wmp-card__info" aria-hidden="true">' + infoIconHtml + '</span>';
    mobileHelp.addEventListener('click', onRxHelpClick);
    mobileStepInfo.appendChild(mobileHelp);
  }

  function removeMobileStepHelp() {
    var existing = document.getElementById(MOBILE_HELP_ID);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }
  }

  /* -----------------------------------------------------------------------
     Helpers
     ----------------------------------------------------------------------- */
  function onRxHelpClick() {
    var flowRoot = document.getElementById('wmp-prescription-flow');
    var overlay = flowRoot && flowRoot.closest('.wmp-flow-overlay');
    var prescriptionDialog = (overlay && overlay.querySelector('#modal-app-prescription')) || document.getElementById('modal-app-prescription');
    if (!prescriptionDialog || typeof prescriptionDialog.showModal !== 'function') return;

    if (!prescriptionDialog.open) {
      prescriptionDialog.showModal();
    }
  }

  var CHEVRON_RIGHT_SVG ='<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function createActionBtn(iconSvg, label, boldText, onClick) {
    var btn = document.createElement('button');
    btn.className = 'wmp-rx-action';
    btn.setAttribute('type', 'button');

    // Left: icon in sand circle
    var iconCircle = document.createElement('span');
    iconCircle.className = 'wmp-rx-action__icon';
    iconCircle.innerHTML = iconSvg;
    btn.appendChild(iconCircle);

    // Center: label text
    var textEl = document.createElement('span');
    textEl.className = 'wmp-rx-action__text';
    textEl.innerHTML = label + (label ? ' ' : '') + '<strong>' + boldText + '</strong>';
    btn.appendChild(textEl);

    // Right: chevron
    var chevron = document.createElement('span');
    chevron.className = 'wmp-rx-action__chevron';
    chevron.innerHTML = CHEVRON_RIGHT_SVG;
    btn.appendChild(chevron);

    btn.addEventListener('click', onClick);
    return btn;
  }

  function createSelect(name, optionsKey, currentValue) {
    var sel = document.createElement('select');
    sel.className = 'wmp-rx-select';
    sel.setAttribute('data-field', name);
    sel.innerHTML = OPTIONS_HTML[optionsKey];

    // Preserve the original axis value for per-eye reset when CYL -> 0
    if (optionsKey === 'axis') {
      sel.setAttribute('data-original', (currentValue !== null && currentValue !== undefined) ? String(currentValue) : '');
    }

    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
      sel.value = String(currentValue);

      // A saved ADD can fall outside the current lens type's range; show the
      // placeholder so the customer picks again instead of a blank select.
      if (isAddField(name) && sel.selectedIndex === -1) {
        sel.value = '';
        return sel;
      }

      // For SPH/CYL, a value of 0 is the default/placeholder state (0 (PL) /
      // Plano / 0 (DS)), not an explicit entry — don't mark it prefilled or
      // hasExplicitSphOrCylSelection() will treat blank prescriptions as filled.
      var isSphOrCyl = isSphOrCylField(name);
      var isDefaultZero = isSphOrCyl && parseFloat(currentValue) === 0;

      if (!isDefaultZero) {
        sel.setAttribute('data-prefilled', '1');
      }
    }
    return sel;
  }

  function buildEyeRow(eye, shortLabel, subLabel, showAdd, values) {
    var tr = document.createElement('tr');
    tr.setAttribute('data-eye', eye);

    var labelTd = document.createElement('td');
    labelTd.className = 'wmp-rx-table__label';
    labelTd.innerHTML = '<strong>' + shortLabel + '</strong><span>' + subLabel + '</span>';
    tr.appendChild(labelTd);

    var fields = [
      { name: eye + '-sph', key: 'sph', val: values.sph },
      { name: eye + '-cyl', key: 'cyl', val: values.cyl },
      { name: eye + '-axis', key: 'axis', val: values.axis }
    ];
    if (showAdd) {
      var addKey = store.get('prescriptionType') === 'progressive' ? 'addProgressive' : 'add';
      fields.push({ name: eye + '-add', key: addKey, val: values.add });
    }

    for (var i = 0; i < fields.length; i++) {
      var td = document.createElement('td');
      td.className = 'wmp-rx-table__cell';
      td.appendChild(createSelect(fields[i].name, fields[i].key, fields[i].val));
      tr.appendChild(td);
    }

    return tr;
  }

  function buildPrismTable(prismValues) {
    var table = document.createElement('table');
    table.className = 'wmp-rx-table wmp-rx-table--prism';

    var thead = document.createElement('thead');
    var htr = document.createElement('tr');
    htr.innerHTML = '<th class="wmp-rx-table__corner"></th>' +
      '<th>Vertical (\u0394)</th><th>Base Direction</th>' +
      '<th>Horz. (\u0394)</th><th>Base Direction</th>';
    thead.appendChild(htr);
    table.appendChild(thead);

    var tbody = document.createElement('tbody');
    tbody.appendChild(buildPrismRow('od', 'OD', 'Right Eye', (prismValues.od || {})));
    tbody.appendChild(buildPrismRow('os', 'OS', 'Left Eye', (prismValues.os || {})));
    table.appendChild(tbody);

    return table;
  }

  function buildPrismRow(eye, shortLabel, subLabel, values) {
    var tr = document.createElement('tr');
    tr.setAttribute('data-eye', eye);

    var labelTd = document.createElement('td');
    labelTd.className = 'wmp-rx-table__label';
    labelTd.innerHTML = '<strong>' + shortLabel + '</strong><span>' + subLabel + '</span>';
    tr.appendChild(labelTd);

    var fields = [
      { name: eye + '-prism-v',     key: 'prismV',    val: values.vertical },
      { name: eye + '-prism-vdir',  key: 'prismVDir', val: values.verticalDir },
      { name: eye + '-prism-h',     key: 'prismH',    val: values.horizontal },
      { name: eye + '-prism-hdir',  key: 'prismHDir', val: values.horizontalDir }
    ];

    for (var i = 0; i < fields.length; i++) {
      var td = document.createElement('td');
      td.className = 'wmp-rx-table__cell';
      td.appendChild(createSelect(fields[i].name, fields[i].key, fields[i].val));
      tr.appendChild(td);
    }

    return tr;
  }

  function onTwoPdToggle(e) {
    var pdRow = document.getElementById('wmp-pd-row');
    var pdLabelEmpty = document.getElementById('wmp-pd-label-empty');
    var lpdHeader = document.getElementById('wmp-lpd-header');
    var rpdHeader = document.getElementById('wmp-rpd-header');
    var rpdRow = document.getElementById('wmp-rpd-row');
    var lpdRow = document.getElementById('wmp-lpd-row');
    var pdSection = pdRow ? pdRow.parentNode : null;
    if (e.target.checked) {
      pdRow.hidden = true;
      if (pdLabelEmpty) pdLabelEmpty.hidden = false;
      if (lpdHeader) lpdHeader.hidden = false;
      if (rpdHeader) rpdHeader.hidden = false;
      if (rpdRow) rpdRow.hidden = false;
      if (lpdRow) lpdRow.hidden = false;
      if (pdSection) pdSection.classList.add('wmp-rx-pd--two-pds');
    } else {
      pdRow.hidden = false;
      if (pdLabelEmpty) pdLabelEmpty.hidden = true;
      if (lpdHeader) lpdHeader.hidden = true;
      if (rpdHeader) rpdHeader.hidden = true;
      if (rpdRow) {
        rpdRow.hidden = true;
        // Clear error highlights from lpd/rpd when switching back to single PD
        rpdRow.classList.remove('wmp-rx-field-error-cell');
        var rpdSel = rpdRow.querySelector('[data-field="rpd"]');
        if (rpdSel) rpdSel.classList.remove('wmp-rx-field-error');
      }
      if (lpdRow) {
        lpdRow.hidden = true;
        lpdRow.classList.remove('wmp-rx-field-error-cell');
        var lpdSel = lpdRow.querySelector('[data-field="lpd"]');
        if (lpdSel) lpdSel.classList.remove('wmp-rx-field-error');
      }
      if (pdSection) pdSection.classList.remove('wmp-rx-pd--two-pds');
    }

    // Update continue button state when two-PD toggle changes
    if (containerRef) {
      var continueBtn = containerRef.querySelector('.wmp-btn--primary.wmp-btn--xl');
      if (continueBtn) {
        syncContinueButtonState(containerRef, continueBtn);
      }
    }
  }

  /* -----------------------------------------------------------------------
     Collect values from selects
     ----------------------------------------------------------------------- */
  function collectValues() {
    var getValue = function (name) {
      var sel = containerRef.querySelector('[data-field="' + name + '"]');
      if (!sel || sel.value === '') return null;
      return parseFloat(sel.value);
    };

    var hasTwoPDs = document.getElementById('wmp-two-pd-check').checked;
    var prismCheck = document.getElementById('wmp-prism-check');
    var hasPrism = !!(getPrismConfig() && prismCheck && prismCheck.checked);

    var getOrderField = function (name) {
      var el = containerRef.querySelector('[data-order-field="' + name + '"]');
      if (!el) return '';
      if (el.type === 'checkbox') return el.checked;
      return (el.value || '').trim();
    };

    var orderAttributes = {
      issueDate: getOrderField('issueDate'),
      doctorName: getOrderField('doctorName'),
      confirmed: !!getOrderField('confirmed')
    };

    return {
      od: {
        sph: getValue('od-sph'),
        cyl: getValue('od-cyl'),
        axis: getValue('od-axis'),
        add: getValue('od-add')
      },
      os: {
        sph: getValue('os-sph'),
        cyl: getValue('os-cyl'),
        axis: getValue('os-axis'),
        add: getValue('os-add')
      },
      pd: hasTwoPDs ? null : getValue('pd'),
      rpd: hasTwoPDs ? getValue('rpd') : null,
      lpd: hasTwoPDs ? getValue('lpd') : null,
      hasTwoPDs: hasTwoPDs,
      hasPrism: hasPrism,
      prism: hasPrism ? {
        od: {
          vertical:    getValue('od-prism-v'),
          verticalDir: (function () { var el = containerRef.querySelector('[data-field="od-prism-vdir"]'); return el ? el.value : null; })(),
          horizontal:    getValue('od-prism-h'),
          horizontalDir: (function () { var el = containerRef.querySelector('[data-field="od-prism-hdir"]'); return el ? el.value : null; })()
        },
        os: {
          vertical:    getValue('os-prism-v'),
          verticalDir: (function () { var el = containerRef.querySelector('[data-field="os-prism-vdir"]'); return el ? el.value : null; })(),
          horizontal:    getValue('os-prism-h'),
          horizontalDir: (function () { var el = containerRef.querySelector('[data-field="os-prism-hdir"]'); return el ? el.value : null; })()
        }
      } : store.get('rxValues').prism,
      orderAttributes: orderAttributes
    };
  }

  function buildServerPayloadFromDom() {
    var out = {};
    if (!containerRef) return out;

    var selects = containerRef.querySelectorAll('select[data-field]');
    for (var i = 0; i < selects.length; i++) {
      var s = selects[i];
      var field = s.getAttribute('data-field');
      var label = '';
      if (s.options && s.selectedIndex > -1) label = s.options[s.selectedIndex].textContent.trim();
      if (!label) label = s.value || '';
      var key = _toServerKey(field);
      out[key] = label;
    }

    return out;
  }

  /* -----------------------------------------------------------------------
     Server integration: load/save customer prescription via /apps/prescription
     ----------------------------------------------------------------------- */
  function _toServerKey(field) {
    // field comes like 'od-sph' or 'pd' or 'rpd' etc. Normalize to server key
    var f = String(field).replace(/-/g, '_');
    if (f === 'rpd') return 'OD_PD';
    if (f === 'lpd') return 'OS_PD';
    if (f === 'pd') return 'PD';

    var parts = f.split('_');
    if (parts.length === 3) {
      // e.g. od_prism_vdir -> OD_PRISM_Vdir-like transform used in legacy code
      var p0 = parts[0].toUpperCase();
      var p1 = parts[1].toUpperCase();
      var p2 = parts[2].charAt(0).toUpperCase() + parts[2].slice(1).toLowerCase();
      return p0 + '_' + p1 + '_' + p2;
    }
    return f.toUpperCase();
  }

  function _fromServerKey(key) {
    if (!key) return null;
    if (key === 'OD_PD') return 'rpd';
    if (key === 'OS_PD') return 'lpd';
    if (key === 'PD') return 'pd';
    var k = String(key).toLowerCase();
    return k.replace(/_/g, '-');
  }

  function _getSelectOptionByLabel(sel, label) {
    if (!sel) return null;
    for (var i = 0; i < sel.options.length; i++) {
      var opt = sel.options[i];
      if (opt.textContent.trim() === label || String(opt.value) === String(label)) return opt.value;
    }
    return null;
  }

  async function fetchSavedPrescription() {
    try {
      var res = await fetch('/apps/prescription');
      if (!res.ok) return null;
      var json = await res.json();
      if (!json || !json.length) return null;
      // legacy endpoint returns array of metafields; value is stored JSON
      var mf = json[0];
      if (!mf || !mf.value) return null;
      try {
        return JSON.parse(mf.value);
      } catch (e) {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  function checkSavedPrescriptionAvailability() {
    if (_savedPrescriptionData) return Promise.resolve(_savedPrescriptionData);
    if (_savedPrescriptionCheckPromise) return _savedPrescriptionCheckPromise;

    _savedPrescriptionCheckPromise = fetchSavedPrescription().then(function (pres) {
      _savedPrescriptionData = pres || null;
      return _savedPrescriptionData;
    }).catch(function () {
      _savedPrescriptionData = null;
      return null;
    }).then(function (pres) {
      _savedPrescriptionCheckPromise = null;
      return pres;
    });

    return _savedPrescriptionCheckPromise;
  }

  function applySavedPrescription(prescriptionData) {
    if (!containerRef || !prescriptionData) return;

    // transform server keys to data-field names and apply
    for (var key in prescriptionData) {
      if (!prescriptionData.hasOwnProperty(key)) continue;
      var local = _fromServerKey(key);
      if (!local) continue;

      // fields in our DOM use data-field names like 'od-sph', 'od-cyl', 'pd', 'rpd', 'lpd'
      var sel = containerRef.querySelector('[data-field="' + local + '"]');
      var value = prescriptionData[key];

      if (sel) {
        // try find option by label first, fallback to value
        var optVal = _getSelectOptionByLabel(sel, value);
        if (optVal !== null) {
          sel.value = optVal;
        } else {
          // try raw value
          try { sel.value = String(value); } catch (e) { /* noop */ }
        }
        if (isAddField(local) && sel.selectedIndex === -1) sel.value = '';

        // trigger change listeners to update state/validation UX
        var ev = new Event('change', { bubbles: true });
        sel.dispatchEvent(ev);
      }
    }

    // If any of the left/right PDs present, toggle two-PD UI
    var rpd = containerRef.querySelector('[data-field="rpd"]');
    var lpd = containerRef.querySelector('[data-field="lpd"]');
    if (rpd && lpd && (rpd.value !== '' || lpd.value !== '')) {
      var twoPd = document.getElementById('wmp-two-pd-check');
      if (twoPd && !twoPd.checked) {
        twoPd.checked = true;
        twoPd.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Prism: if any prism fields not zero / n/a, check prism box
    var prismFields = ['od-prism-v', 'os-prism-v', 'od-prism-h', 'os-prism-h'];
    var hasPrism = false;
    for (var i = 0; i < prismFields.length; i++) {
      var psel = containerRef.querySelector('[data-field="' + prismFields[i] + '"]');
      if (psel && psel.value !== '' && psel.value !== '0' && psel.value !== '0.00') hasPrism = true;
    }
    var prismCheckEl = document.getElementById('wmp-prism-check');
    if (prismCheckEl && hasPrism) {
      prismCheckEl.checked = true;
      prismCheckEl.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Saved prescription should only be pulled after explicit user action.
  function applySavedPrescriptionFromAccount(buttonEl) {
    if (!buttonEl || buttonEl.getAttribute('data-loading') === '1') return;

    buttonEl.setAttribute('data-loading', '1');
    buttonEl.disabled = true;

    checkSavedPrescriptionAvailability().then(function (pres) {
      if (!pres) {
        console.warn('[WMP] No saved prescription found for this account.');
        return;
      }

      applySavedPrescription(pres);
      _savedPrescriptionApplied = true;

      if (containerRef) {
        var continueBtn = containerRef.querySelector('.wmp-btn--primary.wmp-btn--xl');
        if (continueBtn) syncContinueButtonState(containerRef, continueBtn);
      }
    }).catch(function (err) {
      console.warn('[WMP] Failed to load saved prescription.', err);
    }).then(function () {
      buttonEl.disabled = false;
      buttonEl.removeAttribute('data-loading');
    });
  }

  function setSavedRxButtonState(buttonEl, hasSavedRx) {
    if (!buttonEl) return;

    var textEl = buttonEl.querySelector('.wmp-rx-action__text');
    if (textEl) {
      if (hasSavedRx) {
        textEl.innerHTML = '<strong>Use Saved Prescription</strong>';
      } else {
        textEl.innerHTML = '<strong>You\'re Logged In</strong>';
      }
    }

    if (hasSavedRx) {
      buttonEl.disabled = false;
      buttonEl.removeAttribute('aria-disabled');
      buttonEl.style.opacity = '';
      buttonEl.style.cursor = '';
    } else {
      buttonEl.disabled = true;
      buttonEl.setAttribute('aria-disabled', 'true');
      buttonEl.style.opacity = '0.6';
      buttonEl.style.cursor = 'default';
    }
  }

  /* -----------------------------------------------------------------------
     Validation
     ----------------------------------------------------------------------- */
  // Sign-mismatch warnings are soft, modal-based checks. validate() records
  // pending modal payloads; onContinue() decides whether to show them.
  var pendingCylSignMismatchModal = null;
  var pendingSphSignMismatchModal = null;
  var pendingReadingSphModal = null;
  var acknowledgedCylSignMismatch = false;
  var acknowledgedSphSignMismatch = false;
  var acknowledgedReadingSph = false;

  function getModalCopy(modals, key, fallback) {
    return modals[key] ||
      modals[key.replace(/-/g, '_')] ||
      modals[key.replace(/-([a-z])/g, function (_, ch) { return ch.toUpperCase(); })] ||
      fallback;
  }

  var WARNING_MODAL_COPY = {
    cylMismatch: {
      titleKey: 'cyl-warning-modal-title',
      descriptionKey: 'cyl-warning-modal-description',
      fallbackTitle: 'SOMETHING DOESN\'T LOOK QUITE RIGHT',
      fallbackDescription: 'Just a quick check, you entered a (+) cylinder for one eye and a (-) cylinder for the other. Please make sure your prescription values are correct. If you\'re unsure, contact us.'
    },
    sphMixedSign: {
      titleKey: 'sph-warning-modal-title',
      descriptionKey: 'sph-warning-modal-description',
      fallbackTitle: 'SOMETHING DOESN\'T LOOK QUITE RIGHT',
      fallbackDescription: 'Just a quick check, you entered one positive (+) SPH value and one negative (-) SPH value. Please make sure your prescription values are correct. If you\'re unsure, feel free to leave us a message and we\'ll help you out.'
    },
    readingNegativeSph: {
      titleKey: 'sph-sign-modal-title',
      descriptionKey: 'sph-sign-modal-description',
      fallbackTitle: 'SOMETHING DOESN\'T LOOK QUITE RIGHT',
      fallbackDescription: 'Just a quick check, you entered a negative (-) SPH value, but most reading prescriptions have a positive (+) SPH. Take a look at your prescription and make sure everything matches your doctor\'s form. If you\'re unsure, feel free to leave us a message and we\'ll help you out.'
    }
  };

  function getWarningModalCopy(type) {
    var config = WARNING_MODAL_COPY[type] || {};
    var modals = (window.WmpI18n || {}).modals || {};
    return {
      title: getModalCopy(modals, config.titleKey, config.fallbackTitle || ''),
      desc: getModalCopy(modals, config.descriptionKey, config.fallbackDescription || '')
    };
  }

  function validate(values) {
    var errors = [];
    var warnings = [];
    var rxType = store.get('prescriptionType');
    pendingCylSignMismatchModal = null;
    pendingSphSignMismatchModal = null;
    pendingReadingSphModal = null;

    // 1. At least one SPH or CYL value entered
    var hasSphOrCyl = (values.od.sph !== null) || (values.od.cyl !== null) ||
                      (values.os.sph !== null) || (values.os.cyl !== null);
    if (!hasSphOrCyl) {
      errors.push({
        type: 'error',
        msg: 'Please enter at least one SPH or CYL value.',
        fields: ['od-sph', 'od-cyl', 'os-sph', 'os-cyl']
      });
    }

    // 2. CYL mixed signs -> soft warning modal
    if (values.od.cyl !== null && values.os.cyl !== null) {
      var odSign = values.od.cyl > 0 ? 1 : (values.od.cyl < 0 ? -1 : 0);
      var osSign = values.os.cyl > 0 ? 1 : (values.os.cyl < 0 ? -1 : 0);
      if (odSign !== 0 && osSign !== 0 && odSign !== osSign) {
        pendingCylSignMismatchModal = getWarningModalCopy('cylMismatch');
      }
    }

    // 3. SPH mixed signs -> soft warning modal
    if (values.od.sph !== null && values.os.sph !== null) {
      var odSphSign = values.od.sph > 0 ? 1 : (values.od.sph < 0 ? -1 : 0);
      var osSphSign = values.os.sph > 0 ? 1 : (values.os.sph < 0 ? -1 : 0);
      if (odSphSign !== 0 && osSphSign !== 0 && odSphSign !== osSphSign) {
        pendingSphSignMismatchModal = getWarningModalCopy('sphMixedSign');
      }
    }

    // 4. Reading: negative SPH -> soft warning modal
    if (rxType === 'reading') {
      if ((values.od.sph !== null && values.od.sph < 0) || (values.os.sph !== null && values.os.sph < 0)) {
        pendingReadingSphModal = getWarningModalCopy('readingNegativeSph');
      }
    }

    // 5. Progressive/Reading: ADD is required per eye whenever that eye's SPH
    // was explicitly entered. See isAddRequiredForEye() — same rule applies
    // to both progressive and reading lenses.
    var odAddRequired = isAddRequiredForEye(containerRef, 'od');
    var osAddRequired = isAddRequiredForEye(containerRef, 'os');

    if (odAddRequired && (values.od.add === null || values.od.add === undefined)) {
      errors.push({
        type: 'error',
        msg: `Please enter an ADD value for OD (Right Eye) for ${rxType} lenses.`,
        fields: ['od-add']
      });
    }

    if (osAddRequired && (values.os.add === null || values.os.add === undefined)) {
      errors.push({
        type: 'error',
        msg: `Please enter an ADD value for OS (Left Eye) for ${rxType} lenses.`,
        fields: ['os-add']
      });
    }

    // 5a. Prism: when enabled, at least one Vertical/Horizontal magnitude is
    // required, and each filled magnitude requires its paired base direction.
    if (values.hasPrism) {
      var pr = values.prism || {};
      var prOd = pr.od || {};
      var prOs = pr.os || {};
      var prismPairs = [
        { magnitude: prOd.vertical, direction: prOd.verticalDir, directionField: 'od-prism-vdir', label: 'OD Vertical' },
        { magnitude: prOd.horizontal, direction: prOd.horizontalDir, directionField: 'od-prism-hdir', label: 'OD Horizontal' },
        { magnitude: prOs.vertical, direction: prOs.verticalDir, directionField: 'os-prism-vdir', label: 'OS Vertical' },
        { magnitude: prOs.horizontal, direction: prOs.horizontalDir, directionField: 'os-prism-hdir', label: 'OS Horizontal' }
      ];
      var anyPrism = false;

      for (var p = 0; p < prismPairs.length; p++) {
        var pair = prismPairs[p];
        var magnitudeFilled = pair.magnitude !== null && pair.magnitude !== undefined && parseFloat(pair.magnitude) !== 0;
        if (magnitudeFilled) {
          anyPrism = true;
          if (!pair.direction) {
            errors.push({
              type: 'error',
              msg: 'Please select a base direction for ' + pair.label + '.',
              fields: [pair.directionField]
            });
          }
        }
      }

      if (!anyPrism) {
        errors.push({
          type: 'error',
          msg: 'Please enter at least one prism value (Vertical or Horizontal).',
          fields: ['od-prism-v', 'od-prism-h', 'os-prism-v', 'os-prism-h']
        });
      }
    }

    // 5b. Two-PD mode: both halves are required
    if (values.hasTwoPDs) {
      if (values.rpd === null) {
        errors.push({
          type: 'error',
          msg: 'Please enter your Right PD (OD).',
          fields: ['rpd']
        });
      }
      if (values.lpd === null) {
        errors.push({
          type: 'error',
          msg: 'Please enter your Left PD (OS).',
          fields: ['lpd']
        });
      }
    }

    // 6. Issue date validation: must be a valid month, not in the future,
    // and not older than 10 years from the current month.
    var issueDateStr = (values.orderAttributes && values.orderAttributes.issueDate) || '';
    if (issueDateStr && issueDateStr !== '') {
      var idYear = null;
      var idMonth = null;
      if (String(issueDateStr).indexOf('-') !== -1) {
        var parts = String(issueDateStr).split('-');
        idYear = parseInt(parts[0], 10);
        idMonth = parseInt(parts[1], 10);
      } else if (String(issueDateStr).indexOf('/') !== -1) {
        var parts2 = String(issueDateStr).split('/');
        // support MM/YYYY
        idMonth = parseInt(parts2[0], 10);
        idYear = parseInt(parts2[1], 10);
      } else {
        var parsed = new Date(issueDateStr);
        if (!isNaN(parsed.getTime())) {
          idYear = parsed.getFullYear();
          idMonth = parsed.getMonth() + 1;
        }
      }

      if (!idYear || !idMonth || isNaN(idYear) || isNaN(idMonth) || idMonth < 1 || idMonth > 12) {
        errors.push({ type: 'error', msg: 'Please enter a valid prescription issue date.', fields: ['issueDate'] });
      } else {
        var issueDate = new Date(idYear, idMonth - 1, 1);
        var now = new Date();
        var currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        if (issueDate > currentMonthStart) {
          errors.push({ type: 'error', msg: 'Prescription issue date cannot be in the future.', fields: ['issueDate'] });
        } else {
          var cutoff = new Date(currentMonthStart.getFullYear() - 10, currentMonthStart.getMonth(), 1);
          if (issueDate < cutoff) {
            errors.push({ type: 'error', msg: 'Prescription issue date is too old. Please provide a prescription issued within the last 10 years.', fields: ['issueDate'] });
          }
        }
      }
    }

    return { errors: errors, warnings: warnings };
  }

  /* -----------------------------------------------------------------------
     Prescription normalization — convert to minus cylinder format
     ----------------------------------------------------------------------- */
  function normalizeToMinusCyl(sph, cyl, axis) {
    if (cyl !== null && cyl > 0) {
      var newSph = (sph || 0) + cyl;
      var newCyl = -cyl;
      var newAxis = ((axis || 0) + 90) % 180;
      if (newAxis === 0) newAxis = 180;
      return { sph: Math.round(newSph * 100) / 100, cyl: Math.round(newCyl * 100) / 100, axis: newAxis };
    }
    return { sph: sph, cyl: cyl, axis: axis };
  }

  /* -----------------------------------------------------------------------
     Show validation messages
     ----------------------------------------------------------------------- */
  function clearFieldHighlights() {
    if (!containerRef) return;

    var highlightedFields = containerRef.querySelectorAll('.wmp-rx-field-error');
    for (var i = 0; i < highlightedFields.length; i++) {
      highlightedFields[i].classList.remove('wmp-rx-field-error');
    }

    var highlightedCells = containerRef.querySelectorAll('.wmp-rx-field-error-cell');
    for (var j = 0; j < highlightedCells.length; j++) {
      highlightedCells[j].classList.remove('wmp-rx-field-error-cell');
    }
  }

  function highlightFieldByName(fieldName) {
    if (!containerRef || !fieldName) return;

    var fieldEl = containerRef.querySelector('[data-field="' + fieldName + '"]') ||
      containerRef.querySelector('[data-order-field="' + fieldName + '"]');
    if (!fieldEl) return;

    fieldEl.classList.add('wmp-rx-field-error');

    var containerEl = (fieldEl.closest && (
      fieldEl.closest('.wmp-rx-table__cell') ||
      fieldEl.closest('.wmp-rx-pd__value-cell') ||
      fieldEl.closest('.wmp-rx-canada__field') ||
      fieldEl.closest('.wmp-rx-order-attrs__confirm')
    )) || null;

    if (containerEl) {
      containerEl.classList.add('wmp-rx-field-error-cell');
    }
  }

  function applyFieldHighlights(validation) {
    if (!validation || !validation.errors || !validation.errors.length) return;

    for (var i = 0; i < validation.errors.length; i++) {
      var fields = validation.errors[i].fields;
      if (!fields || !fields.length) continue;

      for (var j = 0; j < fields.length; j++) {
        highlightFieldByName(fields[j]);
      }
    }
  }

  function showMessages(validation) {
    var errorBox = document.getElementById('wmp-rx-errors');
    if (!errorBox) return;
    errorBox.innerHTML = '';
    clearFieldHighlights();
    applyFieldHighlights(validation);

    var all = validation.errors.concat(validation.warnings);
    for (var i = 0; i < all.length; i++) {
      var msg = document.createElement('div');
      msg.className = all[i].type === 'error' ? 'wmp-rx-error' : 'wmp-rx-warning';
      msg.textContent = all[i].msg;
      errorBox.appendChild(msg);
    }
  }

  function clearMessages() {
    var errorBox = document.getElementById('wmp-rx-errors');
    if (errorBox) errorBox.innerHTML = '';
    clearFieldHighlights();
  }

  /* -----------------------------------------------------------------------
     Continue handler
     ----------------------------------------------------------------------- */
  function onContinue() {
    if (!isFormValid(containerRef)) {
      // Mark that the user has attempted validation so subsequent field
      // changes trigger AXIS highlighting and the Continue button gating
      // requires meaningful input.
      continueAttempted = true;
      highlightRequiredEmptyFields(containerRef);
      // Re-evaluate the Continue button state (will likely disable it until
      // the user makes changes).
      syncContinueButtonState(containerRef);
      return;
    }

    clearMessages();
    var values = collectValues();
    var result = validate(values);

    // Hard errors block advancement
    if (result.errors.length > 0) {
      continueAttempted = true;
      showMessages(result);
      syncContinueButtonState(containerRef);
      return;
    }

    // CYL mixed signs: soft modal. Show once; UNDERSTOOD acknowledges and
    // re-runs Continue, which now falls through and advances.
    if (pendingCylSignMismatchModal && !acknowledgedCylSignMismatch) {
      continueAttempted = true;
      var cylModal = pendingCylSignMismatchModal;
      highlightFieldByName('od-cyl');
      highlightFieldByName('os-cyl');

      if (window.WmpModal && typeof window.WmpModal.open === 'function') {
        window.WmpModal.open({
          title: cylModal.title,
          content: cylModal.desc,
          size: 'md',
          primaryActionText: 'Understood',
          primaryAction: function () {
            acknowledgedCylSignMismatch = true;
            window.WmpModal.close();
            onContinue();
          }
        });
        return;
      }
      // No modal UI available — never trap the user; treat as acknowledged.
      acknowledgedCylSignMismatch = true;
    }

    // SPH mixed signs: soft modal. Show once; UNDERSTOOD acknowledges and
    // re-runs Continue, which now falls through and advances.
    if (pendingSphSignMismatchModal && !acknowledgedSphSignMismatch) {
      continueAttempted = true;
      var sphSignModal = pendingSphSignMismatchModal;
      highlightFieldByName('od-sph');
      highlightFieldByName('os-sph');

      if (window.WmpModal && typeof window.WmpModal.open === 'function') {
        window.WmpModal.open({
          title: sphSignModal.title,
          content: sphSignModal.desc,
          size: 'md',
          primaryActionText: 'Understood',
          primaryAction: function () {
            acknowledgedSphSignMismatch = true;
            window.WmpModal.close();
            onContinue();
          }
        });
        return;
      }
      // No modal UI available — never trap the user; treat as acknowledged.
      acknowledgedSphSignMismatch = true;
    }

    // Reading negative-SPH: soft modal. Show once; UNDERSTOOD acknowledges and
    // re-runs Continue, which now falls through and advances.
    if (pendingReadingSphModal && !acknowledgedReadingSph) {
      continueAttempted = true;
      var readingSphModal = pendingReadingSphModal;
      highlightFieldByName('od-sph');
      highlightFieldByName('os-sph');

      if (window.WmpModal && typeof window.WmpModal.open === 'function') {
        window.WmpModal.open({
          title: readingSphModal.title,
          content: readingSphModal.desc,
          size: 'md',
          primaryActionText: 'Understood',
          primaryAction: function () {
            acknowledgedReadingSph = true;
            window.WmpModal.close();
            onContinue();
          }
        });
        return;
      }
      // No modal UI available — never trap the user; treat as acknowledged.
      acknowledgedReadingSph = true;
    }

    // Soft warnings are informational and must not block progression.
    if (result.warnings.length > 0) {
      showMessages(result);
    }

    // // Normalize to minus cylinder
    // var odNorm = normalizeToMinusCyl(values.od.sph, values.od.cyl, values.od.axis);
    // var osNorm = normalizeToMinusCyl(values.os.sph, values.os.cyl, values.os.axis);

    // values.od.sph = odNorm.sph;
    // values.od.cyl = odNorm.cyl;
    // values.od.axis = odNorm.axis;
    // values.os.sph = osNorm.sph;
    // values.os.cyl = osNorm.cyl;
    // values.os.axis = osNorm.axis;

    // Save to store
    store.set('rxValues', values);

    if (typeof store.hasRxSessionRecord === 'function' && store.hasRxSessionRecord()) {
      store.persistRx();
    }

    // Save Rx to account if checkbox is checked
    var saveCheck = document.getElementById('wmp-save-rx-check');
    if (saveCheck && saveCheck.checked) {
      // Build payload from DOM (use option labels to preserve formatting)
      try {
        var serverData = buildServerPayloadFromDom();
        // fire-and-forget POST to existing endpoint used by legacy UI
        fetch('/apps/prescription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: serverData })
        }).then(function (resp) {
          if (!resp.ok) throw new Error('Save prescription request failed with status ' + resp.status);
          return resp.json().catch(function () { return null; });
        }).then(function (json) {
          // Keep account-saved data available in this session so returning to Step 2
          // can immediately offer "Use Saved Prescription" without waiting for a new fetch.
          _savedPrescriptionData = serverData;
          _savedPrescriptionCheckPromise = null;
        }).catch(function (err) {
          console.warn('[WMP] Failed saving prescription:', err);
        });
      } catch (e) {
        console.warn('[WMP] Save prescription error', e);
      }
    }

    // Update prism pricing — assign (never increment) so re-entering this step
    // via Back can't stack the surcharge. recalcLenses() folds it into Lenses.
    var pricing = store.get('pricing');
    pricing.prism = values.hasPrism ? getPrismPriceCents() : 0;
    store.set('pricing', pricing);
    store.recalcLenses();

    acknowledgedCylSignMismatch = false;
    acknowledgedSphSignMismatch = false;
    acknowledgedReadingSph = false;
    // Reset continueAttempted for the next step so Continue starts enabled.
    continueAttempted = false;
    router.advance();
  }

  /* -----------------------------------------------------------------------
     Auth modal (UI shell — API calls are TODO)
     ----------------------------------------------------------------------- */
  function openAuthModal() {
    var i18n = window.WmpI18n || {};
    var auth = i18n.auth || {};
    var resumeStep = 'enter-prescription';

    var form = document.createElement('form');
    form.className = 'wmp-auth-form';
    form.method = 'post';
    form.action = '/account/login';
    form.setAttribute('novalidate', 'novalidate');

    var resumeUrl = window.location.href;
    var resumePath = window.location.pathname + window.location.search + window.location.hash;
    try {
      var nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('wmp-rx-resume', '1');
      nextUrl.searchParams.set('wmp-rx-step', resumeStep);
      resumeUrl = nextUrl.toString();
      resumePath = nextUrl.pathname + nextUrl.search + nextUrl.hash;
    } catch (e) {
      // Keep current URL if URL API is unavailable.
    }

    try {
      window.localStorage.setItem('return_to', resumeUrl);
    } catch (e2) {
      // ignore storage failures
    }

    var returnUrlInput = document.createElement('input');
    returnUrlInput.type = 'hidden';
    returnUrlInput.name = 'return_url';
    returnUrlInput.value = resumePath;
    form.appendChild(returnUrlInput);

    var returnToInput = document.createElement('input');
    returnToInput.type = 'hidden';
    returnToInput.name = 'return_to';
    returnToInput.value = resumePath;
    form.appendChild(returnToInput);

    // Email
    var emailLabel = document.createElement('label');
    emailLabel.className = 'wmp-auth-form__label';
    emailLabel.textContent = (auth.email_label || 'Email') + "*";
    form.appendChild(emailLabel);

    var emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.className = 'wmp-rx-input mb-2';
    emailInput.placeholder = 'your@email.com';
    emailInput.id = 'wmp-auth-email';
    emailInput.name = 'customer[email]';
    emailInput.required = true;
    emailInput.autocomplete = 'email';
    emailInput.autocorrect = 'off';
    emailInput.autocapitalize = 'off';
    form.appendChild(emailInput);

    // Password
    var pwLabel = document.createElement('label');
    pwLabel.className = 'wmp-auth-form__label';
    pwLabel.textContent = (auth.password_label || 'Password') + "*";
    form.appendChild(pwLabel);

    var pwInput = document.createElement('input');
    pwInput.type = 'password';
    pwInput.className = 'wmp-rx-input';
    pwInput.id = 'wmp-auth-password';
    pwInput.name = 'customer[password]';
    pwInput.required = true;
    pwInput.autocomplete = 'current-password';
    form.appendChild(pwInput);

    // Error area
    var errorArea = document.createElement('div');
    errorArea.id = 'wmp-auth-error';
    errorArea.className = 'wmp-rx-errors';
    form.appendChild(errorArea);

    // Forgot password link (above sign-in button)
    var forgotLink = document.createElement('a');
    forgotLink.href = '/account/login#recover';
    forgotLink.className = 'wmp-auth-form__forgot-password';
    forgotLink.textContent = auth.forgot_password_text || 'Forgot your password?';
    form.appendChild(forgotLink);

    // Sign In button
    var signInBtn = document.createElement('button');
    signInBtn.className = 'wmp-btn wmp-btn--primary wmp-btn--lg wmp-btn--full mb-2';
    signInBtn.textContent = auth.sign_in_btn || 'SIGN IN';
    signInBtn.type = 'submit';
    signInBtn.style.marginTop = '16px';
    form.appendChild(signInBtn);

    form.addEventListener('submit', function (e) {
      var email = (emailInput.value || '').trim();
      var password = pwInput.value || '';
      var errEl = document.getElementById('wmp-auth-error');

      if (!email || !password) {
        e.preventDefault();
        if (errEl) {
          errEl.innerHTML = '<div class="wmp-rx-error">Please enter email and password.</div>';
        }
        return;
      }

      if (errEl) errEl.innerHTML = '';

      try {
        window.localStorage.setItem('return_to', resumeUrl);
      } catch (e2) {
        // ignore storage failures
      }

      signInBtn.disabled = true;
      signInBtn.setAttribute('aria-busy', 'true');
    });

    // Shop Pay divider
    // var divider = document.createElement('div');
    // divider.style.cssText = 'text-align:center;margin:16px 0;color:var(--wmp-neutral-400);font-size:14px;';
    // divider.textContent = 'or';
    // form.appendChild(divider);

    // Use the same Liquid-rendered provider markup as main-login.
    var flowRoot = document.getElementById('wmp-prescription-flow');
    var providerTemplate = flowRoot && flowRoot.querySelector('#wmp-shop-login-template [name="sign-in-with-shop-provider"]');
    if (providerTemplate) {
      var providerSection = providerTemplate.cloneNode(true);
      form.appendChild(providerSection);

      providerSection.addEventListener('click', function () {
        try {
          window.localStorage.setItem('return_to', resumeUrl);
        } catch (e3) {
          // ignore storage failures
        }

        // Shop login opens its own authentication UI. Close the WMP auth
        // modal first so the two modal layers cannot remain stacked after
        // Shop finishes signing the customer in.
        if (window.WmpModal && typeof window.WmpModal.close === 'function') {
          window.WmpModal.close();
        }
      }, true);

      var loadBtn = setInterval(function () {
        try {
          var shopButton = providerSection.querySelector('shop-login-button');
          if (!shopButton || !shopButton.shadowRoot) return;

          var innerButton = shopButton.shadowRoot.querySelector('button');
          var textInInnerButton = innerButton && innerButton.querySelector('span');

          if (innerButton) {
            clearInterval(loadBtn);
            innerButton.style.width = '100%';
            innerButton.style.height = '44px';

            if (textInInnerButton) {
              textInInnerButton.style.fontFamily = '"Zona Pro", sans-serif';
              textInInnerButton.style.fontSize = '16px';
              textInInnerButton.style.fontWeight = 700;
              textInInnerButton.style.textTransform = 'uppercase';
            }
          }
        } catch (err) {
          // noop
        }
      }, 20);

      setTimeout(function () {
        clearInterval(loadBtn);
      }, 10000);
    }

    WmpModal.open({
      title: auth.sign_in_title || 'Sign In',
      content: form,
      size: 'sm',
      onClose: function () {
        var dlg = document.querySelector('.wmp-modal-dialog');
        if (dlg) dlg.classList.remove('wmp-modal-dialog--auth');
      }
    });

    // Add an auth-specific class to the modal dialog for styling
    var dlg = document.querySelector('.wmp-modal-dialog');
    if (dlg) dlg.classList.add('wmp-modal-dialog--auth');
  }

  function destroy() {
    removeMobileStepHelp();
    var mobileContinueBtn = document.getElementById('wmp-mobile-action');
    if (mobileContinueBtn) {
      mobileContinueBtn.disabled = false;
      mobileContinueBtn.removeAttribute('disabled');
    }
    containerRef = null;
    acknowledgedCylSignMismatch = false;
    acknowledgedSphSignMismatch = false;
    acknowledgedReadingSph = false;
    pendingCylSignMismatchModal = null;
    pendingSphSignMismatchModal = null;
    pendingReadingSphModal = null;
  }

  window.WmpSteps = window.WmpSteps || {};
  window.WmpSteps['enter-prescription'] = {
    render: render,
    destroy: destroy,
    onContinue: onContinue
  };
})();
